/**
 * sessionScoringCriteria — the one place a scoring tool asks "which dimensions
 * did this sitting actually declare?"
 *
 * A session row holds its criteria in two places and they do not always agree:
 *
 *   1. `criteria_config.criteriaIds` / `.weights` — the canonical session-level
 *      column every other surface reads.
 *   2. `mode_config.v3Config.criteria` — where the Event Designer engine keeps
 *      them while the event is being built. The engine mirrors (1) from (2)
 *      (session-engine-v3/store.ts), but that mirror is a separate deferred
 *      write and it does not always land: prod session
 *      63b981a0-bf5f-4aa8-8377-833d81ebdfd5 declared Urgency 50 / Theme 50 in
 *      `v3Config` and carries an empty `criteria_config`, and the room was
 *      asked to score Impact / Effort / Risk instead.
 *
 * So reading only (1) and falling through to a tool's own defaults asks people
 * to score a question their session never posed, and nothing about the stored
 * result says so. Resolve (1) → (2) → defaults, and *tell the caller which one
 * it got* so the screen can say it out loud.
 *
 * Weights are normalised here, once, to whole percentages summing to 100 — the
 * same renormalisation the scoring engine applies (Σw) — so the number a
 * participant is shown is the number their score is multiplied by. A weight the
 * session deliberately set to 0 stays 0; only an *absent* weight gets an equal
 * share. (The one exception is a set that is 0 all the way across, which cannot
 * rank anything — `normalizeCriteriaWeights` splits that evenly.)
 */

import { humanizeAttributeKey, normalizeCriteriaWeights } from './sessionCriteriaFromTrack';

/** Where the criteria a tool is about to score came from. */
export type SessionCriteriaSource =
  /** `criteria_config` — the session's own locked set. */
  | 'session'
  /** `mode_config.v3Config.criteria` — the engine's set; the mirror never landed. */
  | 'engine'
  /** The tool's own defaults. The session declared nothing, anywhere. */
  | 'tool_default';

export interface ScoringCriterion {
  id: string;
  name: string;
  description: string;
  /** Whole percent, normalised so the set sums to 100. */
  weight: number;
}

export interface ResolvedScoringCriteria {
  criteria: ScoringCriterion[];
  source: SessionCriteriaSource;
}

/**
 * Weighted scoring's built-in criteria. The scoring tool falls back to these
 * only for a live run that declared no criteria anywhere. A session that named
 * its own dimensions used to get these three anyway: a sitting set up with
 * Impact and Effort at 50/50 asked the room to score Impact, Effort AND Risk at
 * 34/33/33, so the numbers people entered answered a different question than
 * the one their session asked. Reaching them as a fallback now puts a banner on
 * the scoring screen. The tool's own spec sets
 * `requires_explicit_criteria_set: true`; this trio is a floor, not a default.
 *
 * The Session app is the one place they are declared on purpose: a host who
 * picks weighted scoring there gets exactly this set written to the session's
 * criteria (and is told so on the setup screen), so participants score the
 * dimensions the session declared rather than a fallback.
 */
export const WEIGHTED_SCORING_DEFAULT_CRITERIA: ScoringCriterion[] = [
  { id: 'impact', name: 'Impact', description: '', weight: 34 },
  { id: 'effort', name: 'Effort', description: '', weight: 33 },
  { id: 'risk', name: 'Risk', description: '', weight: 33 },
];

/** The shape this reads off a session row. Deliberately structural, not the
 *  full `PrioritizationSession` — the participant surfaces hold partial rows. */
export interface SessionCriteriaCarrier {
  criteriaConfig?: {
    criteriaIds?: string[] | null;
    weights?: Record<string, number> | null;
  } | null;
  modeConfig?: unknown;
}

interface V3Criterion {
  id?: unknown;
  name?: unknown;
  description?: unknown;
  weight?: unknown;
}

/** Attribute metadata lookup — the workspace attribute registry, when present. */
export interface CriterionLabelSource {
  label?: string;
  description?: string;
}

/**
 * Read `mode_config.v3Config.criteria` off a session row. `v3Config` is written
 * through an `as unknown as SessionModeConfig` cast by the engine and is not on
 * the `SessionModeConfig` type, so it has to be dug out structurally.
 */
export function readEngineCriteria(modeConfig: unknown): V3Criterion[] {
  const v3 = (modeConfig as { v3Config?: { criteria?: unknown } } | null | undefined)?.v3Config;
  const criteria = v3?.criteria;
  return Array.isArray(criteria) ? (criteria as V3Criterion[]) : [];
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Resolve the criteria a scoring tool should put in front of a participant.
 *
 * @param session       the session row (partial is fine)
 * @param toolDefaults  the tool's own fallback set, used only when the session
 *                      declared nothing anywhere
 * @param attributeFor  workspace attribute lookup, for human labels
 */
export function resolveSessionScoringCriteria(
  session: SessionCriteriaCarrier | null | undefined,
  toolDefaults: ScoringCriterion[],
  attributeFor?: (id: string) => CriterionLabelSource | null | undefined,
): ResolvedScoringCriteria {
  const engineCriteria = readEngineCriteria(session?.modeConfig);

  // The engine's own list doubles as a name source for the session path: an
  // authored `crit_a1b2c3` id has no attribute row, and rendering the raw id as
  // a criterion's label is how a live session ends up asking people to score
  // "crit_a1b2c3". The human name is right there in v3Config.
  const engineById = new Map<string, V3Criterion>();
  for (const criterion of engineCriteria) {
    const id = nonEmptyString(criterion.id);
    if (id) engineById.set(id, criterion);
  }

  const nameFor = (id: string): string => {
    const attribute = attributeFor?.(id);
    return (
      nonEmptyString(attribute?.label) ??
      nonEmptyString(engineById.get(id)?.name) ??
      // A criterion typed in a quick app is its own words ("Skills for the
      // role"); only a key (snake_case, no spaces) needs turning into a name.
      (/\s/.test(id) ? id : humanizeAttributeKey(id))
    );
  };
  const descriptionFor = (id: string): string => {
    const attribute = attributeFor?.(id);
    return (
      nonEmptyString(attribute?.description) ??
      nonEmptyString(engineById.get(id)?.description) ??
      ''
    );
  };

  const sessionIds = (session?.criteriaConfig?.criteriaIds ?? []).filter(
    (id): id is string => typeof id === 'string' && id.trim().length > 0,
  );

  if (sessionIds.length > 0) {
    const stored = session?.criteriaConfig?.weights ?? {};
    const equal = 100 / sessionIds.length;
    return {
      source: 'session',
      criteria: normalizeCriteriaWeights(
        sessionIds.map((id) => ({
          id,
          name: nameFor(id),
          // `??`, not `||`: a criterion the session deliberately weighted 0 is
          // not the same as one with no weight recorded, and `||` reinstated it
          // at the full equal share.
          weight: typeof stored[id] === 'number' ? stored[id] : equal,
        })),
      ).map((c, i) => ({ ...c, description: descriptionFor(sessionIds[i]) })),
    };
  }

  const fromEngine = engineCriteria
    .map((criterion) => ({ criterion, id: nonEmptyString(criterion.id) }))
    .filter((entry): entry is { criterion: V3Criterion; id: string } => entry.id !== null);

  if (fromEngine.length > 0) {
    const equal = 100 / fromEngine.length;
    return {
      source: 'engine',
      criteria: normalizeCriteriaWeights(
        fromEngine.map(({ criterion, id }) => ({
          id,
          name: nameFor(id),
          weight: typeof criterion.weight === 'number' ? criterion.weight : equal,
        })),
      ).map((c, i) => ({ ...c, description: descriptionFor(fromEngine[i].id) })),
    };
  }

  return {
    source: 'tool_default',
    criteria: normalizeCriteriaWeights(toolDefaults).map((c, i) => ({
      ...c,
      description: toolDefaults[i]?.description ?? '',
    })),
  };
}
